import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';
import './RolePermissionsManager.css';

const RolePermissionsManager = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  
  const { getAccessToken } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      
      // Fetch all data in parallel
      const [rolesRes, featuresRes, permissionsRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/admin/role-permissions/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/admin/role-permissions/features`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/admin/role-permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setRoles(rolesRes.data.roles);
      setFeatures(featuresRes.data.features);
      setPermissions(permissionsRes.data.permissions);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Super admin access required');
      } else {
        toast.error('Failed to load permissions data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/admin/role-permissions/audit-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data.logs);
      setShowAuditLog(true);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    }
  };

  const handleEditRole = (role) => {
    const rolePermissions = permissions[role] || {
      features: [],
      can_view_financials: false,
      filter_by_assigned_clients: false,
      filter_unsigned_only: false,
      receives_contract_emails: false
    };
    
    setSelectedRole(role);
    setEditedPermissions({
      role: role,
      ...rolePermissions
    });
  };

  const handleToggleFeature = (featureId) => {
    if (!editedPermissions) return;
    
    const currentFeatures = editedPermissions.features || [];
    const newFeatures = currentFeatures.includes(featureId)
      ? currentFeatures.filter(f => f !== featureId)
      : [...currentFeatures, featureId];
    
    setEditedPermissions({
      ...editedPermissions,
      features: newFeatures
    });
  };

  const handleToggleOption = (option) => {
    if (!editedPermissions) return;
    
    setEditedPermissions({
      ...editedPermissions,
      [option]: !editedPermissions[option]
    });
  };

  const handleSave = async () => {
    if (!editedPermissions || !selectedRole) return;
    
    try {
      setSaving(true);
      const token = await getAccessToken();
      
      await axios.put(
        `${API_ENDPOINTS.API_BASE_URL}/api/admin/role-permissions/${selectedRole}`,
        editedPermissions,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Permissions updated for ${selectedRole}`);
      
      // Clear dynamic permissions cache so changes take effect
      try {
        const { clearDynamicPermissionsCache, getDynamicPermissions } = await import('../../utils/dynamicPermissions');
        clearDynamicPermissionsCache();
        // Reload permissions immediately
        const newPerms = await getDynamicPermissions();
        if (newPerms) {
          window.__DYNAMIC_PERMISSIONS__ = newPerms;
          console.log('[PERMISSIONS] Reloaded after update');
        }
      } catch (e) {
        console.warn('[PERMISSIONS] Could not reload:', e);
      }
      
      // Refresh permissions
      await fetchData();
      
      // Close edit modal
      setSelectedRole(null);
      setEditedPermissions(null);
    } catch (error) {
      console.error('Error saving permissions:', error);
      
      // Even if there's an error, try to refresh data (save might have succeeded)
      try {
        await fetchData();
        // If refresh succeeds, the save probably worked
        toast.warning('Permissions may have been saved. Please verify the changes.');
        setSelectedRole(null);
        setEditedPermissions(null);
      } catch (refreshError) {
        // If refresh also fails, show the original error
        toast.error('Failed to save permissions');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedRole(null);
    setEditedPermissions(null);
  };

  if (loading) {
    return (
      <div className="role-permissions-manager">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading permissions data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-permissions-manager">
      <div className="rpm-header">
        <h1>Role Permissions Manager</h1>
        <p className="rpm-subtitle">Manage role-based access control for all features</p>
        <button 
          className="audit-log-button" 
          onClick={fetchAuditLogs}
        >
          View Audit Log
        </button>
      </div>

      <div className="roles-grid">
        {roles.map(role => {
          const rolePerms = permissions[role] || { features: [] };
          const featureCount = rolePerms.features?.length || 0;
          
          return (
            <div key={role} className="role-card">
              <div className="role-card-header">
                <h3>{role.toUpperCase()}</h3>
                <span className="feature-count">{featureCount} features</span>
              </div>
              
              <div className="role-card-body">
                <div className="permission-flags">
                  {rolePerms.can_view_financials && (
                    <span className="flag">💰 Financials</span>
                  )}
                  {rolePerms.filter_by_assigned_clients && (
                    <span className="flag">👥 Assigned Only</span>
                  )}
                  {rolePerms.filter_unsigned_only && (
                    <span className="flag">📝 Unsigned Only</span>
                  )}
                  {rolePerms.receives_contract_emails && (
                    <span className="flag">📧 Contract Emails</span>
                  )}
                </div>
                
                <div className="feature-list">
                  {rolePerms.features?.map(featureId => {
                    const feature = features.find(f => f.feature_id === featureId);
                    return feature ? (
                      <div key={featureId} className="feature-item">
                        ✓ {feature.feature_name}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              
              {/* Scroll indicator - shows on hover if content overflows */}
              {featureCount > 7 && (
                <div className="scroll-indicator active">
                  <svg width="24" height="40" viewBox="0 0 24 40" fill="none">
                    <path d="M7 28l5 5 5-5" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
              
              <div className="role-card-footer">
                <button 
                  className="edit-role-button"
                  onClick={() => handleEditRole(role)}
                >
                  Edit Permissions
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      {selectedRole && editedPermissions && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Permissions: {selectedRole.toUpperCase()}</h2>
              <button className="close-button" onClick={handleCancel}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Permission Options */}
              <div className="permission-options">
                <h3>Permission Options</h3>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editedPermissions.can_view_financials || false}
                    onChange={() => handleToggleOption('can_view_financials')}
                  />
                  <span>Can View Financials</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editedPermissions.filter_by_assigned_clients || false}
                    onChange={() => handleToggleOption('filter_by_assigned_clients')}
                  />
                  <span>Filter By Assigned Clients Only</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editedPermissions.filter_unsigned_only || false}
                    onChange={() => handleToggleOption('filter_unsigned_only')}
                  />
                  <span>Filter Unsigned Only</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editedPermissions.receives_contract_emails || false}
                    onChange={() => handleToggleOption('receives_contract_emails')}
                  />
                  <span>📧 Get Contract Approval Emails</span>
                </label>
              </div>

              {/* Feature Selection */}
              <div className="feature-selection">
                <h3>Features</h3>
                <div className="features-grid">
                  {features.map(feature => (
                    <label key={feature.feature_id} className="feature-checkbox">
                      <input
                        type="checkbox"
                        checked={(editedPermissions.features || []).includes(feature.feature_id)}
                        onChange={() => handleToggleFeature(feature.feature_id)}
                      />
                      <div className="feature-info">
                        <span className="feature-name">{feature.feature_name}</span>
                        {feature.description && (
                          <span className="feature-description">{feature.description}</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className="save-button" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditLog && (
        <div className="modal-overlay" onClick={() => setShowAuditLog(false)}>
          <div className="audit-log-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Permission Changes Audit Log</h2>
              <button className="close-button" onClick={() => setShowAuditLog(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="audit-log-list">
                {auditLogs.length === 0 ? (
                  <p className="no-logs">No audit logs yet</p>
                ) : (
                  auditLogs.map((log, index) => (
                    <div key={index} className="audit-log-entry">
                      <div className="log-header">
                        <span className="log-action">{log.action.toUpperCase()}</span>
                        <span className="log-role">{log.role}</span>
                        <span className="log-timestamp">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="log-details">
                        <p><strong>Admin:</strong> {log.admin_username}</p>
                        <p><strong>Features:</strong> {log.changes.features?.length || 0}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionsManager;

